import { useMutation } from '@tanstack/react-query'
import { linkEmail } from '@/lib/api-client'
import { sanitizeOutlookHtml, makeSnippet, truncateBodyText } from '@/lib/sanitize'
import type { LinkableEmail, AttachmentMeta } from '@/types'

export interface LinkEmailResult {
  id: string
}

function toAddressList(list: { email: string; name?: string }[]) {
  // Filter out any entries with empty email (would fail Zod .email() validation)
  return list
    .filter((a) => a.email)
    .map((a) => ({
      email: a.email,
      ...(a.name ? { name: a.name } : {}),
    }))
}

function toAttachmentList(list: AttachmentMeta[]) {
  return list
    .filter((a) => !a.isInline)
    .map((a) => ({ filename: a.name, mimeType: a.contentType, size: a.size }))
}

// Convert null to undefined so Zod .optional() fields accept the value.
// JSON.stringify omits undefined keys, so this effectively removes the field from the body.
function orUndef(v: string | null | undefined): string | undefined {
  return v ?? undefined
}

export function useLinkEmail() {
  return useMutation<
    LinkEmailResult,
    { message: string; status: number },
    {
      matterId: string
      email: LinkableEmail
      direction?: 'inbound' | 'outbound'
      matterDocumentIds?: string[]
      alwaysAutoFile?: boolean
    }
  >({
    mutationFn: async ({
      matterId,
      email,
      direction = 'inbound',
      matterDocumentIds,
      alwaysAutoFile,
    }) => {
      // fromAddress must be a valid email or omitted — empty string fails Zod .email()
      const fromAddress = email.from?.email || undefined
      const fromName = email.from?.name || undefined

      const result = await linkEmail(matterId, {
        messageId: orUndef(email.internetMessageId),
        threadId: orUndef(email.conversationId),
        subject: orUndef(email.subject),
        fromAddress,
        fromName,
        toAddresses: toAddressList(email.to),
        ccAddresses: toAddressList(email.cc),
        snippet: makeSnippet(email.bodyText),
        bodyText: truncateBodyText(email.bodyText),
        bodyHtml: sanitizeOutlookHtml(email.bodyHtml) ?? undefined,
        attachments: toAttachmentList(email.attachments),
        sentAt: orUndef(email.dateTimeCreated),
        source: 'extension',
        direction,
        ...(matterDocumentIds && matterDocumentIds.length > 0
          ? { matterDocumentIds }
          : {}),
        ...(alwaysAutoFile ? { alwaysAutoFile: true } : {}),
      })

      if (result.error) {
        throw { message: result.error, status: result.status }
      }

      return result.data!
    },
  })
}
